import {
  Fragment,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react"

import { cn } from "../../lib/utils"
import { Tooltip } from "./tooltip"

export type TableColumn<T> = {
  key: string
  /** Optional responsive styling shared by this column header and cells. */
  className?: string
  header: string
  render: (row: T) => ReactNode
  /** Horizontal alignment of this column's header + cells. Defaults to `"left"`;
   *  use `"right"` for numeric columns. */
  align?: "left" | "right"
  /** Spells out what the column measures, on hover/focus of the header. For
   *  columns whose heading cannot carry the definition — which of two ranks,
   *  against which baseline, over which window. */
  headerHint?: string
}

type TableProps<T> = {
  columns: readonly TableColumn<T>[]
  rows: readonly T[]
  rowKey: (row: T) => string | number
  empty?: ReactNode
  /** Inline feedback directly after a data row. */
  rowDetail?: (row: T) => ReactNode
  tableClassName?: string
  /** A row link supplies keyboard access; retain native table semantics. */
  rowLinkTarget?: boolean
  className?: string
  /** Vertical alignment of cell content within a row. Defaults to `"top"`;
   *  use `"middle"` to center single-line cells against taller stacked ones. */
  cellAlign?: "top" | "middle"
  /** Denser typography + padding (text-xs) for nested / detail tables.
   *  Defaults to `false` (text-sm) — the standard top-level table size. */
  dense?: boolean
  /** Removes the outer surface so the table can sit inside a parent workbench. */
  embedded?: boolean
  /**
   * Called for every row with the current row and the previous row (undefined
   * for the first row). Return a label (or any node) to insert a separator
   * row above `current`, or null to render nothing. The label appears on a
   * thicker divider line that spans all columns.
   */
  rowSeparator?: (current: T, previous: T | undefined) => ReactNode | null
  rowClassName?: (row: T) => string | undefined
  isRowInteractive?: (row: T) => boolean
  onRowClick?: (row: T) => void
  /** Animate newly rendered top-level rows with a short, capped stagger. */
  animateRows?: boolean
  /** Stack each row into labelled fields below `sm`; desktop stays a table. */
  stackOnMobile?: boolean
}

const SCROLL_TABLE_CLASSES = {
  container: "overflow-x-auto",
  table: "",
  head: "",
  body: "",
  row: "",
  cell: "",
} as const

const STACKED_TABLE_CLASSES = {
  container: "overflow-visible sm:overflow-x-auto",
  table: "block sm:table",
  head: "hidden sm:table-header-group",
  body: "block sm:table-row-group",
  row: "block px-4 py-1.5 sm:table-row sm:px-0 sm:py-0",
  cell: "grid grid-cols-[minmax(6rem,0.7fr)_minmax(0,1fr)] gap-3 sm:table-cell",
} as const

export function Table<T>({
  columns,
  rows,
  rowKey,
  empty = "No rows.",
  rowDetail,
  className,
  tableClassName,
  rowLinkTarget = false,
  cellAlign = "top",
  dense = false,
  embedded = false,
  rowSeparator,
  rowClassName,
  isRowInteractive,
  onRowClick,
  animateRows = false,
  stackOnMobile = false,
}: TableProps<T>) {
  const layoutClasses = stackOnMobile ? STACKED_TABLE_CLASSES : SCROLL_TABLE_CLASSES
  if (rows.length === 0) {
    return (
      <div
        className={cn(
          "bg-card py-10 text-center text-sm text-muted-foreground",
          embedded ? "" : "rounded-xl border border-dashed border-border"
        )}
      >
        {empty}
      </div>
    )
  }

  const cellPadding = tableCellPadding(dense, stackOnMobile)

  return (
    <div
      className={cn(
        "bg-card",
        layoutClasses.container,
        embedded ? "" : "rounded-xl border border-border shadow-card",
        className
      )}
    >
      <table
        className={cn(
          "min-w-full",
          dense ? "text-xs" : "text-sm",
          layoutClasses.table,
          tableClassName
        )}
      >
        <thead className={cn("bg-muted/35 text-left", layoutClasses.head)}>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={cn(
                  cellPadding,
                  "border-b border-border text-[11px] font-semibold text-muted-foreground",
                  column.align === "right" && "text-right",
                  column.className
                )}
              >
                {column.headerHint === undefined ? (
                  column.header
                ) : (
                  <Tooltip content={column.headerHint}>
                    <span className="cursor-help underline decoration-dotted decoration-from-font underline-offset-[3px]">
                      {column.header}
                    </span>
                  </Tooltip>
                )}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className={layoutClasses.body}>
          {rows.map((row, i) => {
            const separator = rowSeparator?.(row, rows[i - 1]) ?? null
            const detail = rowDetail?.(row)
            const interactive = Boolean(onRowClick && (isRowInteractive?.(row) ?? true))
            return (
              <Fragment key={rowKey(row)}>
                {separator !== null ? (
                  <tr className="group bg-background/60">
                    <td
                      colSpan={columns.length}
                      className="border-t border-border px-4 py-1.5 text-xs font-medium text-muted-foreground"
                    >
                      {separator}
                    </td>
                  </tr>
                ) : null}

                <tr
                  className={cn(
                    "border-t border-border/70 transition-colors hover:bg-info/[0.035]",
                    layoutClasses.row,
                    animateRows && "motion-list-row",
                    interactive &&
                      "cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                    rowClassName?.(row)
                  )}
                  style={rowAnimationStyle(animateRows, i)}
                  {...(interactive ? interactiveRowProps(row, onRowClick, rowLinkTarget) : {})}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      data-label={column.header}
                      className={cn(
                        cellPadding,
                        layoutClasses.cell,
                        cellAlign === "middle" ? "align-middle" : "align-top",
                        column.align === "right" && "text-right",
                        column.className
                      )}
                    >
                      {stackOnMobile ? (
                        <>
                          <span className="text-[11px] font-semibold text-muted-foreground sm:hidden">
                            {column.header}
                          </span>

                          <span className="min-w-0 sm:contents">{column.render(row)}</span>
                        </>
                      ) : (
                        column.render(row)
                      )}
                    </td>
                  ))}
                </tr>

                {detail ? (
                  <tr className="table-row-detail">
                    <td colSpan={columns.length}>{detail}</td>
                  </tr>
                ) : null}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function tableCellPadding(dense: boolean, stackOnMobile: boolean): string {
  if (!stackOnMobile) {
    return dense ? "px-4 py-2.5" : "px-5 py-3.5"
  }
  return dense ? "px-0 py-2 sm:px-4 sm:py-2.5" : "px-0 py-2 sm:px-5 sm:py-3.5"
}

function interactiveRowProps<T>(
  row: T,
  onRowClick: ((row: T) => void) | undefined,
  rowLinkTarget: boolean
) {
  if (rowLinkTarget) {
    return {
      onClick: (event: MouseEvent) => {
        if (event.target instanceof Element && event.target.closest("a,button,input")) {
          return
        }
        onRowClick?.(row)
      },
    }
  }
  return {
    role: "button" as const,
    tabIndex: 0,
    onClick: () => onRowClick?.(row),
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return
      }
      event.preventDefault()
      onRowClick?.(row)
    },
  }
}

function rowAnimationStyle(animateRows: boolean, index: number): CSSProperties | undefined {
  if (!animateRows) {
    return undefined
  }
  return { animationDelay: `${Math.min(index, 7) * 24}ms` }
}
